import MultiTabComponent from "@/manager/components/multi-tab-component/multi-tab-component";
import Page from "@/manager/components/page/page";
import { useNavigate, useParams } from "react-router";
import DatasetConfigurationTab from "./configuration-tab";
import DatasetLabelsTab from "./dataset-labels-tab";
import { failedToast, getAllData, postSingle } from "@/shared/utils/api";
import useHttp from "@/shared/hooks/use-http";
import { FormEvent, useContext, useEffect, useState } from "react";
import { AuthContext } from "@/shared/context/auth-context";
import { Button, Form } from "react-bootstrap";
import { BsSave } from "react-icons/bs";
import "./dataset-edit.scss";
import { createUUID, validateLoadingStatusNew } from "@/shared/utils/utils";
import Spinner from "@/shared/components/spinner/spinner";

export interface IDatasetLabel {
  id: string;
  amount_direction_id?: string;
  datamodel_field_id: string;
  datamodel_id?: string;
  datamodel_type?: string;
  datamodel_reference_field_id?: string;
  datamodel_referencing_component_group_id?: string;
  label?: string;
  negative?: string;
  positive?: string;
  datamodel_field_type: string;
  field_name: string;
  empty_values?: number;
  null_values?: number;
  distinct_values?: number;
  total_rows?: number;
}

export interface IDataset {
  id: string;
  dataset_name: string;
  event_id: string;
  model_type: string;
  tmsconfig_id: string;
  timeframe_from: string | undefined;
  timeframe_to: string | undefined;
  selected_template_layout_id: string | undefined;
  selected_pdf_template_layout_id: string | undefined;
  labels: IDatasetLabel[];
}

const DatasetEdit = () => {
  const params = useParams();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);

  const [datasetData, setDatasetData] = useState<IDataset>({
    id: "",
    dataset_name: "",
    event_id: "",
    timeframe_from: "",
    timeframe_to: "",
    model_type: "",
    tmsconfig_id: "",
    selected_template_layout_id: undefined,
    selected_pdf_template_layout_id: undefined,
    labels: []
  });

  const [possibleLabels, setPossibleLabels] = useState<IDatasetLabel[]>([]);
  const [validated, setValidated] = useState(false);
  const [hasLinkedAlert, setHasLinkedAlert] = useState(false);

  const { sendRequest: getDataset, status: getDatasetStatus, data: getDatasetData,  error: getDatasetError } = useHttp(getAllData, false, authCtx.token, "dataset", "analytics");
  const { sendRequest: getEvent, status: getEventStatus, data: getEventData,  error: getEventError } = useHttp(getAllData, false, authCtx.token, "event", "platform");
  const { sendRequest: getDatasetLabels, status: getDatasetLabelsStatus, data: getDatasetLabelsData,  error: getDatasetLabelsError } = useHttp(getAllData, false, authCtx.token, "datasetLabels", "analytics");
  const { sendRequest: postDataset, status: postDatasetStatus, data: postDatasetData, error: postDatasetError } = useHttp(postSingle, false, authCtx.token, "dataset", "analytics");
  
  useEffect(() => {
    if (params.id) {
      getDataset(`dataset/${params.id}`, undefined, undefined, "none");
    }
  }, [params.id]);

  useEffect(() => {
    if (datasetData.event_id) {
      let url = `dataset_labels/${datasetData.event_id}`;
      if (params.id) {
        url = `${url}/${params.id}`;
      }
      getDatasetLabels(url, undefined, undefined, "none");
      getEvent(`datamodel/event/${datasetData.event_id}`, undefined, undefined, "none");
    }
  }, [datasetData.event_id]);

  useEffect(() => {
    if (getEventStatus === "completed" && !getEventError && getEventData) {
      setHasLinkedAlert(!!getEventData.alertcase_id);
    }
  }, [getEventStatus, getEventData, getEventError]);

  useEffect(() => {
    if (getDatasetLabelsStatus === "completed" && !getDatasetLabelsError && getDatasetLabelsData) {
      setPossibleLabels(getDatasetLabelsData);
      if (!params.id) {
        setDatasetData({
          ...datasetData,
          labels: getDatasetLabelsData.filter((label: IDatasetLabel) => label.datamodel_type === "event")
        });
      }
    }
  }, [getDatasetLabelsStatus, getDatasetLabelsData, getDatasetLabelsError]);

  useEffect(() => {
    if (getDatasetStatus === "completed" && !getDatasetError && getDatasetData) {
      setDatasetData({
        id: getDatasetData.id,
        dataset_name: getDatasetData.dataset_name,
        event_id: getDatasetData.event_id,
        timeframe_from: getDatasetData.timeframe_from,
        timeframe_to: getDatasetData.timeframe_to,
        model_type: getDatasetData.model_type,
        tmsconfig_id: getDatasetData.tmsconfig_id,
        selected_template_layout_id: getDatasetData.selected_template_layout_id,
        selected_pdf_template_layout_id: getDatasetData.selected_pdf_template_layout_id,
        labels: getDatasetData.labels
      });
    }
  }, [getDatasetStatus, getDatasetData, getDatasetError]);

  useEffect(() => {
    if (postDatasetStatus === "completed" && !postDatasetError) {
      navigate(`/analytics/datasets-details/${postDatasetData.id}`);
    }
  }, [postDatasetStatus, postDatasetError]);

  const saveData = () => {
    const postData = {
      ...datasetData
    };
    postData.labels = datasetData.labels.filter(label => label.label && label.label !== "");
    postData.id = datasetData.id ? datasetData.id : createUUID();
    postDataset(`dataset/${postData.id}`, postData, undefined, undefined);
  };

  const handleSubmit = (event?: FormEvent<HTMLFormElement>) => {
    if (!event) {
      return;
    }
    setValidated(true);
    event.preventDefault();
    const form = event?.currentTarget;

    if (!form.checkValidity()) {
      return;
    }

    const requiredPropertyCheck = [
      {key: 'dataset_name', name: 'Dataset name'},
      {key: 'event_id', name: 'Event type'},
      {key: 'model_type', name: 'Model type'},
      {key: 'tmsconfig_id', name: 'TMS config settings'},
      {key: 'labels', name: 'Labels'}].filter(prop => {
      return datasetData[prop.key as keyof IDataset] === undefined || datasetData[prop.key as keyof IDataset] === "";
    });
    
    const requiredLabels = ['entity', 'amount', 'datetime'];
    if (!hasLinkedAlert) {
      requiredLabels.push('suspicious');
    }
    const requiredLabelCheck = requiredLabels.filter(labelName => !datasetData.labels.some(label => label.label === labelName));

    if (requiredPropertyCheck.length > 0) {
      failedToast(`Please fill in all required fields: ${requiredPropertyCheck.map(prop => prop.name).join(", ")}`);
      return;
    }

    if (requiredLabelCheck.length > 0) {
      failedToast(`Please create the following required labels: ${requiredLabelCheck.join(", ")}`);
      return;
    }
    saveData();
  };

  return  <Page title={{
      text: "Dataset"
    }}
    breadCrumbItems={[
      {
        name: "Datasets",
        url: "/analytics/datasets"
      },
      {
        name: "Dataset",
        url: `/analytics/datasets-details/${params.id}`
      },
      {
        name: "Edit Dataset"
      }
    ]}
  >
    <div className="datasetEdit">
      {validateLoadingStatusNew([getDatasetStatus, getDatasetLabelsStatus, postDatasetStatus]) && <Spinner />}
      <Form className="datasetEdit-form" noValidate validated={validated} onSubmit={handleSubmit}>
        <Button type="submit" className="datasetEdit-save" variant="primary">
          <BsSave/>
        </Button>
        <MultiTabComponent tabs={[{
          name: "Configuration",
          key: "configuration",
          content: <DatasetConfigurationTab
            data={datasetData}
            setData={setDatasetData}
          />
        }, {
          name: "Datalabels",
          key: "datalabels",
          content: <DatasetLabelsTab
            data={datasetData}
            setData={setDatasetData}
            possibleLabels={possibleLabels}
            hasLinkedAlert={hasLinkedAlert}
          />
        }]} />
      </Form>
    </div>
  </Page>
};

export default DatasetEdit;